<template>
  <div ref="root" class="alice-kb">
    <header class="alice-kb__head">
      <div class="alice-kb__head-main">
        <div class="alice-kb__crumb">
          <span>Admin</span>
          <Icon name="chevron-right" :size="12" />
          <span>Kennisbank</span>
        </div>
        <h1 class="alice-kb__title">Kennisbank</h1>
        <p class="alice-kb__subtitle">
          Content for ai.valsplat.team, the internal AI knowledge site.
          Colleagues see your changes on their next visit.
        </p>
      </div>
      <button class="alice-kb__refresh" :disabled="anyLoading" @click="refresh">
        {{ anyLoading ? "Loading…" : "Refresh" }}
      </button>
    </header>

    <div class="alice-kb__tabs" role="tablist">
      <button
        v-for="t in sectionTabs"
        :key="t.key"
        type="button"
        class="alice-kb__tab"
        :class="{ 'alice-kb__tab--active': section === t.key }"
        role="tab"
        :aria-selected="section === t.key"
        @click="section = t.key"
      >
        <span>{{ t.label }}</span>
        <span
          v-if="t.count != null"
          class="alice-kb__tab-count"
          :class="{ 'alice-kb__tab-count--alert': t.alert }"
          >{{ t.count }}</span
        >
        <span
          v-if="t.dirty"
          class="alice-kb__tab-dirty"
          title="Unsaved changes"
        />
      </button>
    </div>

    <div class="alice-kb__panel">
      <!-- ================= Skills ================= -->
      <section v-if="section === 'skills'">
        <div class="alice-kb__section-head">
          <p class="alice-kb__hint">
            A phase and a short summary (about 200 characters) per published
            skill. The site groups skills by phase. The placeholder is the first
            sentence of the skill's own description.
          </p>
          <button
            class="alice-kb__primary"
            :disabled="!dirtySkillIds.length || skillsSaving"
            @click="saveSkills"
          >
            {{ skillsSaveLabel }}
          </button>
        </div>
        <div v-if="errors.skills" class="alice-kb__error" role="alert">
          {{ errors.skills }}
        </div>

        <div v-if="loading.skills && !skills.length" class="alice-kb__state">
          Loading skills…
        </div>
        <div v-else-if="!skills.length" class="alice-kb__state">
          No published skills.
        </div>
        <div v-else class="alice-kb__table">
          <div class="alice-kb__srow alice-kb__srow--head">
            <div>Skill</div>
            <div>Phase</div>
            <div>Summary</div>
          </div>
          <div
            v-for="s in skills"
            :key="s.id"
            class="alice-kb__srow"
            :class="{ 'alice-kb__srow--dirty': isSkillDirty(s) }"
          >
            <div class="alice-kb__skill">
              <div class="alice-kb__skill-title">{{ s.title }}</div>
              <div v-if="s.category" class="alice-kb__skill-cat">
                {{ s.category }}
              </div>
            </div>
            <div>
              <select
                v-model="s.phase"
                class="alice-kb__select"
                :aria-label="`Phase for ${s.title}`"
              >
                <option :value="null">No phase</option>
                <option v-for="p in phases" :key="p.value" :value="p.value">
                  {{ p.label }}
                </option>
              </select>
            </div>
            <div>
              <textarea
                v-model="s.summary"
                class="alice-kb__input alice-kb__input--area"
                rows="2"
                :placeholder="s.placeholder || 'Short description'"
                :aria-label="`Summary for ${s.title}`"
              />
              <div
                class="alice-kb__counter"
                :class="{
                  'alice-kb__counter--over': s.summary.length > summaryMax,
                }"
              >
                {{ s.summary.length }}/{{ summaryMax }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ================= Tools ================= -->
      <section v-else-if="section === 'tools'">
        <div class="alice-kb__section-head">
          <p class="alice-kb__hint">
            AI tools on the site, in ascending order. The body is markdown: how
            to get access and what the tool is good for.
          </p>
          <button
            class="alice-kb__primary"
            :disabled="!!toolDraft"
            @click="newTool"
          >
            <Icon name="plus" :size="14" />
            <span>Add tool</span>
          </button>
        </div>
        <div v-if="errors.tools" class="alice-kb__error" role="alert">
          {{ errors.tools }}
        </div>

        <div v-if="toolDraft" ref="toolEditor" class="alice-kb__card">
          <div class="alice-kb__card-head">
            <span class="alice-kb__card-title">{{
              toolDraft.id ? `Edit “${toolDraft.name || "tool"}”` : "New tool"
            }}</span>
          </div>
          <div class="alice-kb__form">
            <div class="alice-kb__form-row alice-kb__form-row--3">
              <label class="alice-kb__field">
                <span>Name</span>
                <input
                  v-model="toolDraft.name"
                  class="alice-kb__input"
                  type="text"
                  placeholder="Alice"
                />
              </label>
              <label class="alice-kb__field">
                <span>Icon</span>
                <select v-model="toolDraft.icon" class="alice-kb__select">
                  <option v-for="i in toolIcons" :key="i" :value="i">
                    {{ i }}
                  </option>
                </select>
              </label>
              <label class="alice-kb__field">
                <span>Order</span>
                <input
                  v-model.number="toolDraft.order"
                  class="alice-kb__input"
                  type="number"
                  step="1"
                />
              </label>
            </div>
            <label class="alice-kb__field">
              <span>Summary (one sentence)</span>
              <input
                v-model="toolDraft.summary"
                class="alice-kb__input"
                type="text"
              />
            </label>
            <div class="alice-kb__form-row">
              <label class="alice-kb__field">
                <span>URL (optional)</span>
                <input
                  v-model="toolDraft.url"
                  class="alice-kb__input"
                  type="url"
                  placeholder="https://"
                />
              </label>
              <label class="alice-kb__field">
                <span>Link label (optional)</span>
                <input
                  v-model="toolDraft.linkLabel"
                  class="alice-kb__input"
                  type="text"
                  placeholder="Open Alice"
                />
              </label>
            </div>
            <label class="alice-kb__field">
              <span>Body (markdown)</span>
              <textarea
                v-model="toolDraft.body"
                class="alice-kb__input alice-kb__input--area alice-kb__input--mono"
                rows="12"
                spellcheck="false"
              />
            </label>
          </div>
          <div class="alice-kb__card-foot">
            <span v-if="toolDraftError" class="alice-kb__foot-error">{{
              toolDraftError
            }}</span>
            <button
              type="button"
              class="alice-kb__secondary"
              :disabled="toolSaving"
              @click="toolDraft = null"
            >
              Cancel
            </button>
            <button
              type="button"
              class="alice-kb__primary"
              :disabled="toolSaving || !!toolDraftError"
              @click="saveTool"
            >
              {{ toolSaving ? "Saving…" : "Save tool" }}
            </button>
          </div>
        </div>

        <div v-if="loading.tools && !tools.length" class="alice-kb__state">
          Loading tools…
        </div>
        <div v-else-if="!tools.length && !toolDraft" class="alice-kb__state">
          No tools yet. Click “Add tool” to create one.
        </div>
        <div v-else-if="tools.length" class="alice-kb__table">
          <div
            v-for="t in tools"
            :key="t.id"
            class="alice-kb__item"
            :class="{
              'alice-kb__item--editing': toolDraft && toolDraft.id === t.id,
            }"
          >
            <div class="alice-kb__item-order">{{ t.order }}</div>
            <div class="alice-kb__item-main">
              <div class="alice-kb__item-title">
                {{ t.name || "Untitled" }}
                <span class="alice-kb__tag">{{ t.icon }}</span>
              </div>
              <div v-if="t.summary" class="alice-kb__item-sub">
                {{ t.summary }}
              </div>
              <a
                v-if="safeUrl(t.url)"
                class="alice-kb__link"
                :href="safeUrl(t.url)"
                target="_blank"
                rel="noopener noreferrer"
                >{{ t.linkLabel || t.url }}</a
              >
            </div>
            <div class="alice-kb__actions">
              <button
                class="alice-kb__action"
                :disabled="!!toolDraft"
                @click="editTool(t)"
              >
                <Icon name="pencil" :size="13" />
                <span>Edit</span>
              </button>
              <button
                class="alice-kb__action alice-kb__action--danger"
                :disabled="busyId === t.id"
                @click="removeTool(t)"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- ================= Pages ================= -->
      <section v-else-if="section === 'pages'">
        <div class="alice-kb__section-head">
          <p class="alice-kb__hint">
            The site's editable pages. Intro is plain text; the body is
            markdown.
          </p>
        </div>
        <div v-if="errors.pages" class="alice-kb__error" role="alert">
          {{ errors.pages }}
        </div>
        <div v-if="loading.pages && !pagesLoaded" class="alice-kb__state">
          Loading pages…
        </div>
        <template v-else>
          <div v-for="p in pages" :key="p.id" class="alice-kb__card">
            <div class="alice-kb__card-head">
              <span class="alice-kb__card-title">{{ p.label }}</span>
              <span class="alice-kb__muted"
                >kb_pages/{{ p.id }} ·
                {{
                  p.exists ? `saved ${fmtDate(p.updatedAt)}` : "not created yet"
                }}</span
              >
            </div>
            <div class="alice-kb__form">
              <label class="alice-kb__field">
                <span>Title</span>
                <input
                  v-model="p.draft.title"
                  class="alice-kb__input"
                  type="text"
                />
              </label>
              <label class="alice-kb__field">
                <span>Intro</span>
                <textarea
                  v-model="p.draft.intro"
                  class="alice-kb__input alice-kb__input--area"
                  rows="3"
                />
              </label>
              <label class="alice-kb__field">
                <span>Body (markdown)</span>
                <textarea
                  v-model="p.draft.body"
                  class="alice-kb__input alice-kb__input--area alice-kb__input--mono"
                  rows="16"
                  spellcheck="false"
                />
              </label>
            </div>
            <div class="alice-kb__card-foot">
              <button
                type="button"
                class="alice-kb__primary"
                :disabled="p.saving || (p.exists && !isPageDirty(p))"
                @click="savePage(p)"
              >
                {{
                  p.saving
                    ? "Saving…"
                    : !p.exists
                    ? "Create page"
                    : isPageDirty(p)
                    ? "Save page"
                    : "Saved"
                }}
              </button>
            </div>
          </div>
        </template>
      </section>

      <!-- ================= Videos ================= -->
      <section v-else-if="section === 'videos'">
        <div class="alice-kb__section-head">
          <p class="alice-kb__hint">
            YouTube suggestions from colleagues. Only approved videos show on
            the site.
          </p>
        </div>
        <div v-if="errors.videos" class="alice-kb__error" role="alert">
          {{ errors.videos }}
        </div>
        <div v-if="loading.videos && !videos.length" class="alice-kb__state">
          Loading videos…
        </div>
        <template v-else>
          <div v-for="g in videoGroups" :key="g.status" class="alice-kb__group">
            <div class="alice-kb__group-title">
              {{ g.label }}
              <span class="alice-kb__muted">{{ g.items.length }}</span>
            </div>
            <div v-if="!g.items.length" class="alice-kb__empty">None.</div>
            <div v-else class="alice-kb__table">
              <div v-for="v in g.items" :key="v.id" class="alice-kb__video">
                <component
                  :is="safeUrl(v.url) ? 'a' : 'div'"
                  class="alice-kb__thumb"
                  v-bind="
                    safeUrl(v.url)
                      ? {
                          href: safeUrl(v.url),
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        }
                      : {}
                  "
                >
                  <img
                    v-if="v.youtubeId"
                    :src="thumbUrl(v)"
                    alt=""
                    loading="lazy"
                  />
                  <Icon v-else name="video" :size="20" />
                </component>

                <div class="alice-kb__item-main">
                  <template v-if="videoDraft && videoDraft.id === v.id">
                    <label class="alice-kb__field">
                      <span>Title</span>
                      <input
                        v-model="videoDraft.title"
                        class="alice-kb__input"
                        type="text"
                        maxlength="200"
                      />
                    </label>
                    <label class="alice-kb__field">
                      <span>Why watch it</span>
                      <textarea
                        v-model="videoDraft.why"
                        class="alice-kb__input alice-kb__input--area"
                        rows="3"
                      />
                    </label>
                    <div class="alice-kb__inline-actions">
                      <button
                        type="button"
                        class="alice-kb__secondary"
                        :disabled="busyId === v.id"
                        @click="videoDraft = null"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        class="alice-kb__primary"
                        :disabled="busyId === v.id || !videoDraft.title.trim()"
                        @click="saveVideo(v)"
                      >
                        Save
                      </button>
                    </div>
                  </template>
                  <template v-else>
                    <div class="alice-kb__item-title">
                      <a
                        v-if="safeUrl(v.url)"
                        class="alice-kb__title-link"
                        :href="safeUrl(v.url)"
                        target="_blank"
                        rel="noopener noreferrer"
                        >{{ v.title || v.url }}</a
                      >
                      <span v-else>{{ v.title || v.url || "Untitled" }}</span>
                    </div>
                    <div v-if="v.why" class="alice-kb__item-sub">
                      {{ v.why }}
                    </div>
                    <div class="alice-kb__meta">
                      Suggested by {{ v.suggestedByName || "unknown" }}
                      <template v-if="v.createdAt">
                        · {{ fmtDate(v.createdAt) }}</template
                      >
                      <template v-if="v.reviewedAt">
                        · reviewed {{ fmtDate(v.reviewedAt) }}</template
                      >
                    </div>
                  </template>
                </div>

                <div class="alice-kb__actions">
                  <button
                    v-if="v.status !== 'approved'"
                    class="alice-kb__action alice-kb__action--ok"
                    :disabled="busyId === v.id"
                    @click="reviewVideo(v, 'approved')"
                  >
                    Approve
                  </button>
                  <button
                    v-if="v.status !== 'rejected'"
                    class="alice-kb__action"
                    :disabled="busyId === v.id"
                    @click="reviewVideo(v, 'rejected')"
                  >
                    Reject
                  </button>
                  <button
                    class="alice-kb__action"
                    :disabled="busyId === v.id || !!videoDraft"
                    title="Edit title and why"
                    @click="editVideo(v)"
                  >
                    <Icon name="pencil" :size="13" />
                  </button>
                  <button
                    class="alice-kb__action alice-kb__action--danger"
                    :disabled="busyId === v.id"
                    @click="removeVideo(v)"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </section>

      <!-- ================= Wishes ================= -->
      <section v-else-if="section === 'wishes'">
        <div class="alice-kb__section-head">
          <p class="alice-kb__hint">
            Colleagues' wishes for new skills, most votes first. The status
            shows on the site and saves as soon as you change it.
          </p>
        </div>
        <div v-if="errors.wishes" class="alice-kb__error" role="alert">
          {{ errors.wishes }}
        </div>
        <div v-if="loading.wishes && !wishes.length" class="alice-kb__state">
          Loading wishes…
        </div>
        <div v-else-if="!wishes.length" class="alice-kb__state">
          No wishes yet.
        </div>
        <div v-else class="alice-kb__table">
          <div v-for="w in wishes" :key="w.id" class="alice-kb__wish">
            <div class="alice-kb__votes">
              <span class="alice-kb__votes-n">{{ w.votes }}</span>
              <span>{{ w.votes === 1 ? "vote" : "votes" }}</span>
            </div>
            <div class="alice-kb__item-main">
              <div class="alice-kb__item-title">
                {{ w.title || "Untitled" }}
              </div>
              <div v-if="w.details" class="alice-kb__item-sub">
                {{ w.details }}
              </div>
              <div class="alice-kb__meta">
                {{ w.createdByName || "unknown" }}
                <template v-if="w.createdAt">
                  · {{ fmtDate(w.createdAt) }}</template
                >
              </div>
            </div>
            <div class="alice-kb__actions">
              <select
                class="alice-kb__select alice-kb__select--compact"
                :value="w.status"
                :disabled="busyId === w.id"
                :aria-label="`Status of ${w.title}`"
                @change="setWishStatus(w, $event.target.value)"
              >
                <option
                  v-for="st in wishStatuses"
                  :key="st.value"
                  :value="st.value"
                >
                  {{ st.label }}
                </option>
              </select>
              <button
                class="alice-kb__action alice-kb__action--danger"
                :disabled="busyId === w.id"
                @click="removeWish(w)"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db, auth } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";
import {
  KB_PHASES,
  KB_SUMMARY_MAX,
  KB_TOOL_ICONS,
  KB_PAGES,
  KB_WISH_STATUSES,
  toMillis,
  safeUrl,
  skillRows,
  skillMetaPayload,
  isSkillMetaDirty,
  sortTools,
  nextToolOrder,
  toolPayload,
  toolError,
  pagePayload,
  groupVideos,
  countVotes,
  rankWishes,
} from "@/utils/kennisbank";

const SECTIONS = ["skills", "tools", "pages", "videos", "wishes"];
const emptyFlags = () => Object.fromEntries(SECTIONS.map((k) => [k, false]));
const emptyErrors = () => Object.fromEntries(SECTIONS.map((k) => [k, ""]));
const pageFields = (d) => ({
  title: (d && d.title) || "",
  intro: (d && d.intro) || "",
  body: (d && d.body) || "",
});

export default {
  name: "AdminKennisbank",
  components: { Icon },
  beforeRouteLeave() {
    if (this.hasUnsaved) {
      return confirm("You have unsaved Kennisbank changes. Leave anyway?");
    }
    return true;
  },
  data() {
    return {
      section: "skills",
      loading: emptyFlags(),
      errors: emptyErrors(),
      busyId: null,

      // Skills: editable rows + what was last loaded/saved, per skill id.
      skills: [],
      skillsSaved: {},
      skillsSaving: false,

      tools: [],
      toolDraft: null,
      toolDraftOrig: null,
      toolSaving: false,

      pages: [],
      pagesLoaded: false,

      videos: [],
      videoDraft: null,

      wishes: [],

      phases: KB_PHASES,
      summaryMax: KB_SUMMARY_MAX,
      toolIcons: KB_TOOL_ICONS,
      wishStatuses: KB_WISH_STATUSES,
    };
  },
  computed: {
    anyLoading() {
      return SECTIONS.some((k) => this.loading[k]);
    },
    dirtySkillIds() {
      return this.skills.filter((s) => this.isSkillDirty(s)).map((s) => s.id);
    },
    skillsSaveLabel() {
      if (this.skillsSaving) return "Saving…";
      const n = this.dirtySkillIds.length;
      if (!n) return "Saved";
      return `Save ${n} change${n === 1 ? "" : "s"}`;
    },
    dirtyPages() {
      return this.pages.some((p) => this.isPageDirty(p));
    },
    hasUnsaved() {
      return (
        this.dirtySkillIds.length > 0 ||
        this.dirtyPages ||
        this.toolDirty ||
        this.videoDirty
      );
    },
    toolDirty() {
      if (!this.toolDraft) return false;
      return (
        JSON.stringify(toolPayload(this.toolDraft, 0)) !==
        JSON.stringify(toolPayload(this.toolDraftOrig || {}, 0))
      );
    },
    videoDirty() {
      const d = this.videoDraft;
      if (!d) return false;
      return d.title.trim() !== d.orig.title || d.why.trim() !== d.orig.why;
    },
    videoGroups() {
      return groupVideos(this.videos);
    },
    pendingVideoCount() {
      return this.videoGroups[0].items.length;
    },
    toolDraftError() {
      return this.toolDraft ? toolError(this.toolDraft) : "";
    },
    sectionTabs() {
      return [
        {
          key: "skills",
          label: "Skills",
          count: this.skills.length,
          dirty: this.dirtySkillIds.length > 0,
        },
        {
          key: "tools",
          label: "Tools",
          count: this.tools.length,
          dirty: this.toolDirty,
        },
        { key: "pages", label: "Pages", dirty: this.dirtyPages },
        {
          key: "videos",
          label: "Videos",
          count: this.pendingVideoCount,
          alert: this.pendingVideoCount > 0,
        },
        { key: "wishes", label: "Wishes", count: this.wishes.length },
      ];
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    safeUrl,
    uid() {
      return this.$store.state.user?.uid || auth.currentUser?.uid || null;
    },
    errMsg(err) {
      if (err && err.code === "permission-denied") {
        return "No permission: only admins can edit Kennisbank content.";
      }
      return (err && err.message) || String(err);
    },
    fmtDate(v) {
      const ms = toMillis(v);
      if (!ms) return "—";
      return new Date(ms).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    thumbUrl(v) {
      return `https://i.ytimg.com/vi/${encodeURIComponent(
        v.youtubeId
      )}/mqdefault.jpg`;
    },

    refresh() {
      if (
        this.hasUnsaved &&
        !confirm("Discard your unsaved Kennisbank changes and reload?")
      ) {
        return;
      }
      this.toolDraft = null;
      this.videoDraft = null;
      this.loadAll();
    },
    loadAll() {
      this.loadSkills();
      this.loadTools();
      this.loadPages();
      this.loadVideos();
      this.loadWishes();
    },
    async run(key, fn) {
      this.loading[key] = true;
      this.errors[key] = "";
      try {
        await fn();
      } catch (err) {
        console.error(`Kennisbank ${key} load failed:`, err);
        this.errors[key] = `Couldn't load: ${this.errMsg(err)}`;
      } finally {
        this.loading[key] = false;
      }
    },

    // ---------- Skills ----------
    loadSkills() {
      return this.run("skills", async () => {
        const [skillSnap, metaSnap] = await Promise.all([
          getDocs(
            query(collection(db, "skills"), where("status", "==", "published"))
          ),
          getDocs(collection(db, "kb_skill_meta")),
        ]);
        const meta = {};
        metaSnap.docs.forEach((d) => (meta[d.id] = d.data()));
        const rows = skillRows(
          skillSnap.docs.map((d) => ({ ...d.data(), id: d.id })),
          meta
        );
        this.skillsSaved = Object.fromEntries(
          rows.map((r) => [r.id, { phase: r.phase, summary: r.summary }])
        );
        this.skills = rows;
      });
    },
    isSkillDirty(s) {
      return isSkillMetaDirty(s, this.skillsSaved[s.id]);
    },
    async saveSkills() {
      const dirty = this.skills.filter((s) => this.isSkillDirty(s));
      if (!dirty.length) return;
      this.skillsSaving = true;
      this.errors.skills = "";
      try {
        const now = Date.now();
        const uid = this.uid();
        const batch = writeBatch(db);
        const payloads = dirty.map((s) => [s, skillMetaPayload(s, uid, now)]);
        payloads.forEach(([s, payload]) =>
          batch.set(doc(db, "kb_skill_meta", s.id), payload, { merge: true })
        );
        await batch.commit();
        payloads.forEach(([s, payload]) => {
          s.summary = payload.summary;
          this.skillsSaved[s.id] = {
            phase: payload.phase,
            summary: payload.summary,
          };
        });
      } catch (err) {
        console.error("Kennisbank skills save failed:", err);
        this.errors.skills = `Couldn't save: ${this.errMsg(err)}`;
      } finally {
        this.skillsSaving = false;
      }
    },

    // ---------- Tools ----------
    loadTools() {
      return this.run("tools", async () => {
        const snap = await getDocs(collection(db, "kb_tools"));
        this.tools = sortTools(
          snap.docs.map((d) => ({ ...d.data(), id: d.id }))
        );
      });
    },
    newTool() {
      this.toolDraft = {
        id: null,
        name: "",
        summary: "",
        body: "",
        url: "",
        linkLabel: "",
        icon: "star",
        order: nextToolOrder(this.tools),
      };
      this.toolDraftOrig = { ...this.toolDraft };
      this.scrollToToolEditor();
    },
    editTool(t) {
      this.toolDraft = {
        id: t.id,
        name: t.name || "",
        summary: t.summary || "",
        body: t.body || "",
        url: t.url || "",
        linkLabel: t.linkLabel || "",
        icon: KB_TOOL_ICONS.includes(t.icon) ? t.icon : "star",
        order:
          typeof t.order === "number" ? t.order : nextToolOrder(this.tools),
      };
      this.toolDraftOrig = { ...this.toolDraft };
      this.scrollToToolEditor();
    },
    scrollToToolEditor() {
      this.$nextTick(() => {
        const el = this.$refs.toolEditor;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    async saveTool() {
      if (!this.toolDraft || this.toolDraftError) return;
      this.toolSaving = true;
      this.errors.tools = "";
      try {
        const payload = toolPayload(this.toolDraft);
        if (this.toolDraft.id) {
          await setDoc(doc(db, "kb_tools", this.toolDraft.id), payload, {
            merge: true,
          });
        } else {
          await addDoc(collection(db, "kb_tools"), payload);
        }
        this.toolDraft = null;
        await this.loadTools();
      } catch (err) {
        console.error("Kennisbank tool save failed:", err);
        this.errors.tools = `Couldn't save the tool: ${this.errMsg(err)}`;
      } finally {
        this.toolSaving = false;
      }
    },
    async removeTool(t) {
      const label = t.name ? `“${t.name}”` : "this tool";
      if (
        !confirm(`Delete ${label} from the Kennisbank? This can't be undone.`)
      )
        return;
      this.busyId = t.id;
      this.errors.tools = "";
      try {
        await deleteDoc(doc(db, "kb_tools", t.id));
        this.tools = this.tools.filter((x) => x.id !== t.id);
        if (this.toolDraft && this.toolDraft.id === t.id) this.toolDraft = null;
      } catch (err) {
        console.error("Kennisbank tool delete failed:", err);
        this.errors.tools = `Couldn't delete the tool: ${this.errMsg(err)}`;
      } finally {
        this.busyId = null;
      }
    },

    // ---------- Pages ----------
    loadPages() {
      return this.run("pages", async () => {
        const snaps = await Promise.all(
          KB_PAGES.map((p) => getDoc(doc(db, "kb_pages", p.id)))
        );
        this.pages = KB_PAGES.map((p, i) => {
          const snap = snaps[i];
          const data = snap.exists() ? snap.data() : null;
          return {
            ...p,
            exists: !!data,
            updatedAt: data ? data.updatedAt : null,
            draft: pageFields(data),
            saved: pageFields(data),
            saving: false,
          };
        });
        this.pagesLoaded = true;
      });
    },
    isPageDirty(p) {
      const a = pagePayload(p.draft, 0);
      const b = pagePayload(p.saved, 0);
      return a.title !== b.title || a.intro !== b.intro || a.body !== b.body;
    },
    async savePage(p) {
      p.saving = true;
      this.errors.pages = "";
      try {
        const payload = pagePayload(p.draft);
        await setDoc(doc(db, "kb_pages", p.id), payload, { merge: true });
        const fields = pageFields(payload);
        p.draft = { ...fields };
        p.saved = { ...fields };
        p.exists = true;
        p.updatedAt = payload.updatedAt;
      } catch (err) {
        console.error("Kennisbank page save failed:", err);
        this.errors.pages = `Couldn't save “${p.label}”: ${this.errMsg(err)}`;
      } finally {
        p.saving = false;
      }
    },

    // ---------- Videos ----------
    loadVideos() {
      return this.run("videos", async () => {
        const snap = await getDocs(collection(db, "kb_videos"));
        this.videos = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      });
    },
    async updateVideo(v, changes, what) {
      this.busyId = v.id;
      this.errors.videos = "";
      try {
        await updateDoc(doc(db, "kb_videos", v.id), changes);
        Object.assign(v, changes);
        return true;
      } catch (err) {
        console.error(`Kennisbank video ${what} failed:`, err);
        this.errors.videos = `Couldn't ${what} “${
          v.title || "video"
        }”: ${this.errMsg(err)}`;
        return false;
      } finally {
        this.busyId = null;
      }
    },
    reviewVideo(v, status) {
      return this.updateVideo(
        v,
        { status, reviewedAt: Date.now(), reviewedBy: this.uid() },
        status === "approved" ? "approve" : "reject"
      );
    },
    editVideo(v) {
      const title = v.title || "";
      const why = v.why || "";
      this.videoDraft = {
        id: v.id,
        title,
        why,
        orig: { title: title.trim(), why: why.trim() },
      };
    },
    async saveVideo(v) {
      if (!this.videoDraft) return;
      const ok = await this.updateVideo(
        v,
        {
          title: this.videoDraft.title.trim().slice(0, 200),
          why: this.videoDraft.why.trim(),
        },
        "save"
      );
      if (ok) this.videoDraft = null;
    },
    async removeVideo(v) {
      const label = v.title ? `“${v.title}”` : "this video";
      if (!confirm(`Delete ${label}? This can't be undone.`)) return;
      this.busyId = v.id;
      this.errors.videos = "";
      try {
        await deleteDoc(doc(db, "kb_videos", v.id));
        this.videos = this.videos.filter((x) => x.id !== v.id);
        if (this.videoDraft && this.videoDraft.id === v.id) {
          this.videoDraft = null;
        }
      } catch (err) {
        console.error("Kennisbank video delete failed:", err);
        this.errors.videos = `Couldn't delete the video: ${this.errMsg(err)}`;
      } finally {
        this.busyId = null;
      }
    },

    // ---------- Wishes ----------
    loadWishes() {
      return this.run("wishes", async () => {
        const [wishSnap, voteSnap] = await Promise.all([
          getDocs(collection(db, "kb_wishes")),
          getDocs(collectionGroup(db, "kb_votes")),
        ]);
        const counts = countVotes(
          voteSnap.docs.map(
            (d) => d.ref.parent.parent && d.ref.parent.parent.id
          )
        );
        this.wishes = rankWishes(
          wishSnap.docs.map((d) => ({ ...d.data(), id: d.id })),
          counts
        );
      });
    },
    async setWishStatus(w, status) {
      const previous = w.status;
      w.status = status;
      this.busyId = w.id;
      this.errors.wishes = "";
      try {
        await updateDoc(doc(db, "kb_wishes", w.id), { status });
      } catch (err) {
        console.error("Kennisbank wish status failed:", err);
        w.status = previous;
        this.errors.wishes = `Couldn't change the status of “${
          w.title || "wish"
        }”: ${this.errMsg(err)}`;
      } finally {
        this.busyId = null;
      }
    },
    async removeWish(w) {
      const label = w.title ? `“${w.title}”` : "this wish";
      if (!confirm(`Delete ${label}? This can't be undone.`)) return;
      this.busyId = w.id;
      this.errors.wishes = "";
      try {
        await deleteDoc(doc(db, "kb_wishes", w.id));
        this.wishes = this.wishes.filter((x) => x.id !== w.id);
      } catch (err) {
        console.error("Kennisbank wish delete failed:", err);
        this.errors.wishes = `Couldn't delete the wish: ${this.errMsg(err)}`;
      } finally {
        this.busyId = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.alice-kb {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-kb__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-kb__head-main {
  flex: 1;
  min-width: 0;
}
.alice-kb__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-kb__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-kb__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 620px;
  line-height: 1.5;
}
.alice-kb__refresh {
  flex-shrink: 0;
  padding: 8px 16px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
}
.alice-kb__refresh:hover:not(:disabled) {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-kb__refresh:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ============== Section tabs ============== */
.alice-kb__tabs {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 0 32px;
  height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
  overflow-x: auto;
}
.alice-kb__tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 18px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-3);
  white-space: nowrap;
  transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease;
}
.alice-kb__tab:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-kb__tab--active,
.alice-kb__tab--active:hover {
  color: var(--vp-black);
  border-bottom-color: var(--accent-100);
  font-weight: 700;
  background: transparent;
}
.alice-kb__tab-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-3);
}
.alice-kb__tab-count--alert {
  min-width: 18px;
  padding: 1px 6px;
  background: var(--vp-black);
  color: var(--vp-white);
  text-align: center;
}
.alice-kb__tab-dirty {
  width: 6px;
  height: 6px;
  background: var(--accent-100);
  display: inline-block;
}

/* ============== Panel ============== */
.alice-kb__panel {
  padding: 24px 32px 48px;
}
.alice-kb__section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 16px;
}
.alice-kb__hint {
  margin: 0;
  max-width: 640px;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
  color: var(--fg-2);
}
.alice-kb__state,
.alice-kb__empty {
  padding: 24px 0;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--fg-3);
}
.alice-kb__empty {
  padding: 8px 0 4px;
  font-size: 14px;
}
.alice-kb__error {
  margin-bottom: 16px;
  padding: 10px 14px;
  border: 1px solid var(--vp-rood-100, #d43c3c);
  color: var(--vp-rood-100, #d43c3c);
  font-family: var(--font-body);
  font-size: 14px;
}
.alice-kb__muted {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-3);
  letter-spacing: 0;
  text-transform: none;
}

/* ============== Buttons ============== */
.alice-kb__primary,
.alice-kb__secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 38px;
  padding: 0 16px;
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-kb__primary {
  background: var(--vp-black);
  color: var(--vp-white);
  border: 1px solid var(--vp-black);
}
.alice-kb__primary:hover:not(:disabled) {
  background: var(--accent-100);
  border-color: var(--accent-100);
  color: var(--vp-black);
}
.alice-kb__secondary {
  background: var(--vp-white);
  color: var(--vp-black);
  border: 1px solid var(--border-hairline);
}
.alice-kb__secondary:hover:not(:disabled) {
  border-color: var(--vp-black);
}
.alice-kb__primary:disabled,
.alice-kb__secondary:disabled {
  opacity: 0.45;
  cursor: default;
}
.alice-kb__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}
.alice-kb__action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--vp-black);
}
.alice-kb__action:hover:not(:disabled) {
  border-color: var(--vp-black);
}
.alice-kb__action--ok:hover:not(:disabled) {
  border-color: var(--vp-turquoise-100, #00a8a8);
  color: var(--vp-turquoise-100, #00a8a8);
}
.alice-kb__action--danger:hover:not(:disabled) {
  border-color: var(--vp-rood-100, #d43c3c);
  color: var(--vp-rood-100, #d43c3c);
}
.alice-kb__action:disabled {
  opacity: 0.45;
  cursor: default;
}

/* ============== Inputs ============== */
.alice-kb__input,
.alice-kb__select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-hairline);
  border-radius: 0;
  outline: none;
  background: var(--vp-white);
  color: var(--vp-black);
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.45;
}
.alice-kb__input:focus,
.alice-kb__select:focus {
  border-color: var(--vp-black);
}
.alice-kb__input--area {
  display: block;
  resize: vertical;
}
.alice-kb__input--mono {
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.55;
}
.alice-kb__select {
  appearance: auto;
  cursor: pointer;
}
.alice-kb__select--compact {
  width: auto;
  padding: 4px 8px;
  font-family: var(--font-ui);
  font-size: 12px;
}
.alice-kb__counter {
  margin-top: 4px;
  text-align: right;
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
}
.alice-kb__counter--over {
  color: var(--vp-rood-100, #d43c3c);
  font-weight: 700;
}

/* ============== Tables / rows ============== */
.alice-kb__table {
  border: 1px solid var(--border-hairline);
  border-bottom: none;
  background: var(--vp-white);
}
.alice-kb__srow {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 160px minmax(260px, 2fr);
  gap: 16px;
  align-items: start;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-hairline);
  border-left: 3px solid transparent;
}
.alice-kb__srow--head {
  align-items: center;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
  background: var(--bg-muted);
}
.alice-kb__srow--dirty {
  border-left-color: var(--accent-100);
}
.alice-kb__skill {
  padding-top: 6px;
  min-width: 0;
}
.alice-kb__skill-title {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-kb__skill-cat {
  margin-top: 2px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

.alice-kb__item,
.alice-kb__video,
.alice-kb__wish {
  display: grid;
  gap: 16px;
  align-items: start;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-kb__item {
  grid-template-columns: 40px minmax(0, 1fr) auto;
}
.alice-kb__item--editing {
  background: var(--bg-muted);
}
.alice-kb__video {
  grid-template-columns: 160px minmax(0, 1fr) auto;
}
.alice-kb__wish {
  grid-template-columns: 56px minmax(0, 1fr) auto;
}
.alice-kb__item-order {
  padding-top: 2px;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--fg-3);
}
.alice-kb__item-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.alice-kb__item-title {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-kb__item-sub {
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.5;
  color: var(--fg-2);
  white-space: pre-wrap;
}
.alice-kb__meta {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-kb__tag {
  margin-left: 8px;
  padding: 1px 6px;
  border: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 600;
  color: var(--fg-3);
  vertical-align: middle;
}
.alice-kb__link,
.alice-kb__title-link {
  color: var(--vp-black);
  font-family: var(--font-ui);
  font-size: 13px;
  word-break: break-all;
}
.alice-kb__title-link {
  font-family: inherit;
  font-size: inherit;
  word-break: normal;
  text-decoration: none;
}
.alice-kb__title-link:hover {
  text-decoration: underline;
}

/* Video thumbnails (16:9) */
.alice-kb__thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: var(--bg-muted);
  color: var(--fg-3);
}
.alice-kb__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.alice-kb__inline-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

/* Wish votes */
.alice-kb__votes {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0;
  border: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
}
.alice-kb__votes-n {
  font-size: 18px;
  font-weight: 700;
  color: var(--vp-black);
  line-height: 1.2;
}

/* Groups (videos) */
.alice-kb__group {
  margin-bottom: 28px;
}
.alice-kb__group-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--vp-black);
}

/* ============== Cards / forms ============== */
.alice-kb__card {
  margin-bottom: 24px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  scroll-margin-top: 72px;
}
.alice-kb__card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--bg-muted);
}
.alice-kb__card-title {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--vp-black);
}
.alice-kb__card-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid var(--border-hairline);
}
.alice-kb__foot-error {
  margin-right: auto;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--vp-rood-100, #d43c3c);
}
.alice-kb__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
}
.alice-kb__form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.alice-kb__form-row--3 {
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) 100px;
}
.alice-kb__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.alice-kb__field > span {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
}

@media (max-width: 1279px) {
  .alice-kb__head {
    padding: 24px 20px 20px;
  }
  .alice-kb__tabs {
    padding: 0 20px;
  }
  .alice-kb__panel {
    padding: 20px 20px 40px;
  }
  .alice-kb__srow {
    grid-template-columns: minmax(140px, 1fr) 140px minmax(200px, 2fr);
  }
  .alice-kb__video {
    grid-template-columns: 120px minmax(0, 1fr);
  }
  .alice-kb__video .alice-kb__actions {
    grid-column: 2;
    justify-content: flex-start;
  }
  .alice-kb__thumb {
    width: 120px;
  }
}
</style>
